"use client";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Mail, Phone, Smartphone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { SendOTP } from "@/utils/SendOTP";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import axios from "axios";
import { cn } from "@/lib/utils";

const Login = () => {
  const [contact, setContact] = useState("");
  const [showOTP, setShowOTP] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [resendClicked, setResendClicked] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [isIncorrectOTP, setIsIncorrectOTP] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [step, setStep] = useState("contact");
  const [otpVerified, setOtpVerified] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email");

  const router = useRouter();

  const handleSendOtp = async () => {
    if (!contact.trim()) {
      setLoginError(`Please enter your ${loginMethod}`);
      return;
    }
    setIsLoading(true);
    setLoginError("");
    try {
      const data = await SendOTP(contact);
      if (data?.success) {
        setHasPassword(data.hasPassword || false);
        setShowOTP(true);
        setStep("otp");
        startTimer();
        setResendClicked(true);
        setOtpVerified(false);
        setLoginError("");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setLoginError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 4) {
      setIsIncorrectOTP(true);
      return;
    }
    setIsLoading(true);
    setLoginError("");
    try {
      const response = await axios.post("/api/verify-otp", {
        contact,
        otp: otpCode,
      });
      if (!response.data.valid) {
        setIsIncorrectOTP(true);
        setLoginError(response.data.error || "Invalid OTP");
        setIsLoading(false);
        return;
      }
      setIsIncorrectOTP(false);
      setOtpVerified(true);
      setHasPassword(response.data.hasPassword || false);
      if (response.data.hasPassword) {
        setStep("password");
        setShowOTP(false);
      } else {
        await performLogin();
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setLoginError(error.response?.data?.error || "Failed to verify OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const performLogin = async () => {
    setIsLoading(true);
    setLoginError("");
    try {
      const credentials = {
        redirect: false,
        email: contact,
        ...(hasPassword && password && { password: password }),
        otpVerified: true,
      };
      const result = await signIn("credentials", credentials);
      if (result?.error) {
        console.error("Login error:", result.error);
        setLoginError("Invalid credentials. Please try again.");
        setIsLoading(false);
        return;
      }
      router.push("/");
      router.refresh();
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("An error occurred during login. Please try again.");
      setIsLoading(false);
    }
  };

  const startTimer = () => {
    setTimeLeft(60);
    setTimerRunning(true);
  };

  const resendOTP = () => {
    setTimerRunning(false);
    handleSendOtp();
  };

  const handleBackToOTP = () => {
    setStep("otp");
    setShowOTP(true);
    setPassword("");
    setOtpVerified(false);
  };

  useEffect(() => {
    let timer;
    if (timerRunning && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerRunning(false);
    }
    return () => clearTimeout(timer);
  }, [timeLeft, timerRunning]);

  useEffect(() => {
    setShowOTP(false);
    setOtpCode("");
    setHasPassword(false);
    setPassword("");
    setStep("contact");
    setLoginError("");
    setTimerRunning(false);
    setTimeLeft(60);
    setOtpVerified(false);
  }, [contact]);

  const toggleLoginMethod = (method) => {
    setLoginMethod(method);
    setContact("");
    setLoginError("");
    setStep("contact");
    setShowOTP(false);
    setOtpCode("");
  };

  const renderToggleButtons = () => (
    <div className="flex rounded-xl overflow-hidden border border-brand-light w-full p-1 bg-brand-light/40">
      <button
        onClick={() => toggleLoginMethod("email")}
        className={cn(
          "flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 rounded-lg",
          loginMethod === "email"
            ? "bg-white text-brand-deep shadow-brand-sm border border-brand-light"
            : "bg-transparent text-muted-foreground hover:text-brand-deep"
        )}
      >
        <Mail size={16} />
        Email
      </button>
      <button
        onClick={() => toggleLoginMethod("phone")}
        className={cn(
          "flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 rounded-lg",
          loginMethod === "phone"
            ? "bg-white text-brand-deep shadow-brand-sm border border-brand-light"
            : "bg-transparent text-muted-foreground hover:text-brand-deep"
        )}
      >
        <Smartphone size={16} />
        Phone
      </button>
    </div>
  );

  const renderContactInput = () => (
    <div className="space-y-2 w-full">
      <div className="relative w-full group">
        {loginMethod === "email" ? (
          <Mail
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground group-focus-within:text-brand-sky transition-colors duration-200"
            size={20}
          />
        ) : (
          <Phone
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground group-focus-within:text-brand-sky transition-colors duration-200"
            size={20}
          />
        )}
        <Input
          type={loginMethod === "email" ? "email" : "tel"}
          name="contact"
          value={contact}
          placeholder={
            loginMethod === "email"
              ? "Enter your email address"
              : "Enter your phone number"
          }
          onChange={(e) => setContact(e.target.value)}
          disabled={isLoading}
          className="pl-10 h-12 rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
        />
      </div>
      {loginMethod === "email" && (
        <p className="text-xs text-muted-foreground text-center">
          We'll send a verification code to your email
        </p>
      )}
      {loginMethod === "phone" && (
        <p className="text-xs text-muted-foreground text-center">
          We'll send a verification code via SMS
        </p>
      )}
    </div>
  );

  const renderOTPInput = () => (
    <div className="w-full flex flex-col items-center gap-4">
      <div className="text-center text-brand-deep text-sm flex items-center gap-2 justify-center bg-brand-light px-4 py-2 rounded-full">
        <ShieldCheck size={16} />
        OTP sent successfully
      </div>
      <div className="space-y-3 w-full flex flex-col items-center justify-center">
        <InputOTP
          maxLength={4}
          value={otpCode}
          onChange={(value) => {
            setOtpCode(value);
            setIsIncorrectOTP(false);
          }}
          isError={isIncorrectOTP}
        >
          <InputOTPGroup className="gap-2">
            <InputOTPSlot
              index={0}
              className="w-14 h-14 text-xl rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
            />
            <InputOTPSlot
              index={1}
              className="w-14 h-14 text-xl rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
            />
            <InputOTPSlot
              index={2}
              className="w-14 h-14 text-xl rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
            />
            <InputOTPSlot
              index={3}
              className="w-14 h-14 text-xl rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
            />
          </InputOTPGroup>
        </InputOTP>
        {isIncorrectOTP && (
          <p className="text-red-500 text-sm font-medium">
            Incorrect OTP. Please try again.
          </p>
        )}
        <div className="flex items-center gap-2">
          {resendClicked && timeLeft > 0 ? (
            <p className="text-sm text-muted-foreground">
              Resend OTP available in{" "}
              <span className="text-brand-sky font-semibold">{timeLeft}</span>{" "}
              seconds
            </p>
          ) : (
            <Button
              variant="link"
              onClick={resendOTP}
              className="text-brand-deep hover:text-brand-sky font-medium"
              disabled={isLoading}
            >
              Resend OTP
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const renderPasswordInput = () => (
    <div className="w-full flex flex-col items-center gap-4">
      <div className="text-center text-brand-deep text-sm bg-brand-light px-4 py-2 rounded-full">
        OTP verified! Please enter your password.
      </div>
      <div className="relative w-full group">
        <Lock
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground group-focus-within:text-brand-sky transition-colors duration-200"
          size={20}
        />
        <Input
          type="password"
          name="password"
          value={password}
          placeholder="Enter your password"
          onChange={(e) => setPassword(e.target.value)}
          className="pl-10 h-12 rounded-xl border-border focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all duration-200"
          onKeyPress={(e) => e.key === "Enter" && performLogin()}
          disabled={isLoading}
        />
      </div>
      <Button
        variant="link"
        onClick={handleBackToOTP}
        className="text-brand-deep hover:text-brand-sky text-sm font-medium"
      >
        ← Back to OTP
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 flex flex-col items-center w-full">
      {loginError && (
        <div className="w-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center px-4 py-2.5 rounded-xl">
          {loginError}
        </div>
      )}

      {step === "contact" && (
        <div className="w-full space-y-4">
          {renderToggleButtons()}
          {renderContactInput()}
        </div>
      )}

      {step === "otp" && showOTP && renderOTPInput()}

      {step === "password" && renderPasswordInput()}

      {isLoading ? (
        <div className="flex justify-center py-2">
          <Loader2 className="h-8 w-8 animate-spin text-brand-sky" />
        </div>
      ) : (
        <div className="w-full space-y-3">
          {step === "contact" && (
            <Button
              onClick={handleSendOtp}
              className="w-full h-12 rounded-xl gradient-primary hover:gradient-primary-hover text-white font-semibold shadow-brand hover:shadow-brand-lg transition-all duration-200"
              disabled={!contact.trim()}
            >
              Send OTP
            </Button>
          )}

          {step === "otp" && (
            <div className="w-full space-y-3">
              <Button
                onClick={handleVerifyOTP}
                className="w-full h-12 rounded-xl gradient-primary hover:gradient-primary-hover text-white font-semibold shadow-brand hover:shadow-brand-lg transition-all duration-200"
                disabled={otpCode.length !== 4 || isLoading}
              >
                Verify OTP
              </Button>
              <Button
                variant="link"
                onClick={() => {
                  setStep("contact");
                  setContact("");
                }}
                className="w-full text-muted-foreground hover:text-brand-deep text-sm font-medium"
              >
                ← Change {loginMethod}
              </Button>
            </div>
          )}

          {step === "password" && (
            <Button
              onClick={performLogin}
              className="w-full h-12 rounded-xl gradient-sky hover:opacity-95 text-white font-semibold shadow-brand-sky transition-all duration-200"
              disabled={!password}
            >
              Login
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Login;